import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  deleteAdditionalCoupon,
  resetCouponToNotShared,
  updateCouponPhoneAndMemo,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  useGiftCouponSearchHook,
  useGiftCouponStatisticsHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Rejected:
      return { text: '발송불가', className: 'bg-orange-100 text-orange-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    case GiftCouponRedeemStatus.Disposed:
      return { text: '폐기', className: 'bg-slate-200 text-slate-600' };
    default:
      return { text: '대기', className: 'bg-blue-100 text-blue-800' };
  }
};

const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}시`;
};

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const GiftCouponHistorySearchPage: FC = function GiftCouponHistorySearchPage() {
  const queryClient = useQueryClient();
  useGiftCouponStatisticsHook();
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const [showBlacklist, setShowBlacklist] = useState(false);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);
  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 검색 입력 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ordererName, setOrdererName] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [fullCode, setFullCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [redeemStatus, setRedeemStatus] = useState<string>('');

  // 정렬 상태 (신청일시만 정렬 지원)
  type SortDirection = 'asc' | 'desc';
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = useCallback(() => {
    setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
  }, []);

  // 검색 실행 상태
  const [searchKey, setSearchKey] = useState('');
  const [searchParams, setSearchParams] = useState<GiftCouponSearchParams | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useGiftCouponSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  const handleSearch = useCallback(() => {
    if (
      !phoneNumber &&
      !ordererName &&
      !ordererPhone &&
      !fullCode &&
      !dateFrom &&
      !dateTo &&
      !redeemStatus
    ) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftCouponSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (ordererName) params.ordererName = ordererName;
    if (ordererPhone) params.ordererPhone = ordererPhone.replace(/[^0-9]/g, '');
    if (fullCode) params.fullCode = fullCode.trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (redeemStatus) params.redeemStatus = redeemStatus as GiftCouponRedeemStatus;

    const key = JSON.stringify(params);
    setSearchKey(key);
    setSearchParams(params);
    setHasSearched(true);
  }, [phoneNumber, ordererName, ordererPhone, fullCode, dateFrom, dateTo, redeemStatus]);

  const searchByStatus = useCallback((status: GiftCouponRedeemStatus) => {
    setPhoneNumber('');
    setOrdererName('');
    setOrdererPhone('');
    setFullCode('');
    setDateFrom('');
    setDateTo('');
    setRedeemStatus(status);
    setTimeout(() => {
      const params: GiftCouponSearchParams = { redeemStatus: status };
      const key = JSON.stringify(params);
      setSearchKey(key);
      setSearchParams(params);
      setHasSearched(true);
    }, 0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const results = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      const aDate = a.recipient?.requestedAt;
      const bDate = b.recipient?.requestedAt;
      const aTime = aDate ? new Date(aDate).getTime() : 0;
      const bTime = bDate ? new Date(bDate).getTime() : 0;
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [searchResults, sortDirection]);

  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  const getOrdererInfo = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
    return {
      name: order.bugo?.member?.fullName || '-',
      phone: order.bugo?.member?.phoneNumber || '-',
      purchasedAt: order.paymentDetail?.purchasedAt,
    };
  }, []);

  // 선택 상태
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isUnsharing, setIsUnsharing] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectable = results.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Available,
    );
    setSelectedIds((prev) => {
      if (selectable.length > 0 && selectable.every((c) => prev.has(c._id))) {
        return new Set();
      }
      return new Set(selectable.map((c) => c._id));
    });
  }, [results]);

  const selectedShareableCount = useMemo(() => {
    return results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Available,
    ).length;
  }, [results, selectedIds]);

  const handleUnshare = useCallback(async () => {
    const targets = results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Available,
    );
    if (targets.length === 0) {
      alert('미전달 상태로 변경할 쿠폰을 선택해주세요.');
      return;
    }
    if (
      !confirm(
        `선택한 ${targets.length}건의 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?`,
      )
    ) {
      return;
    }

    setIsUnsharing(true);
    try {
      const promises = targets.map((c) => resetCouponToNotShared(c._id));
      await Promise.all(promises);
      showToast(`${targets.length}건 미전달 처리 완료`);
      setSelectedIds(new Set());
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      alert('미전달 처리 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setIsUnsharing(false);
    }
  }, [results, selectedIds, showToast, queryClient]);

  const getItemName = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  const getChoiceUnitName = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  const getImageUrl = useCallback((coupon: GiftCouponForRedemption) => {
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    if (typeof coupon.giftItem === 'object') {
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl =
          typeof giftItemImg === 'object' && 'url' in giftItemImg
            ? giftItemImg.url
            : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl =
          typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
            ? giftItemInfoImg.url
            : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  const [editingCouponId, setEditingCouponId] = useState<string | null>(null);
  const [editPhone, setEditPhone] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const startEditPhoneMemo = useCallback((coupon: GiftCouponForRedemption) => {
    setEditingCouponId(coupon._id);
    setEditPhone(coupon.additionalRecipientPhone?.replace(/[^0-9]/g, '') || '');
    setEditMemo(coupon.memo || '');
  }, []);

  const cancelEditPhoneMemo = useCallback(() => {
    setEditingCouponId(null);
    setEditPhone('');
    setEditMemo('');
  }, []);

  const saveEditPhoneMemo = useCallback(async () => {
    if (!editingCouponId) return;
    setIsSavingEdit(true);
    try {
      await updateCouponPhoneAndMemo(editingCouponId, {
        additionalRecipientPhone: editPhone || '',
        memo: editMemo || '',
      });
      showToast('저장되었습니다.');
      setEditingCouponId(null);
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      alert(message);
      console.error(err);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingCouponId, editPhone, editMemo, showToast, queryClient]);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteAdditional = useCallback(
    async (coupon: GiftCouponForRedemption) => {
      if (
        !confirm(
          `재발행 쿠폰 [${
            coupon.fullCode || coupon.code
          }]을(를) 폐기하시겠습니까?\nDB에서 완전 삭제되며 복구할 수 없습니다.`,
        )
      )
        return;

      setDeletingId(coupon._id);
      try {
        await deleteAdditionalCoupon(coupon._id);
        showToast('재발행 쿠폰이 폐기되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '폐기 처리 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setDeletingId(null);
      }
    },
    [showToast, queryClient],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">쿠폰 내역 검색</h1>
            <p className="mt-1 text-sm text-gray-500">
              기간, 전화번호, 주문자명 등으로 쿠폰 내역을 검색하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-redemption"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            발송 대기 목록
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-7 gap-3 p-4">
        <div className="rounded-lg bg-yellow-50 p-3 text-center">
          <p className="text-lg font-bold text-yellow-700">
            {statistics?.pending ?? '-'}
          </p>
          <p className="text-xs text-yellow-600">발송 대기</p>
        </div>
        <div className="rounded-lg bg-green-50 p-3 text-center">
          <p className="text-lg font-bold text-green-700">
            {statistics?.delivered ?? '-'}
          </p>
          <p className="text-xs text-green-600">발송 완료</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 text-center">
          <p className="text-lg font-bold text-gray-700">{statistics?.total ?? '-'}</p>
          <p className="text-xs text-gray-600">전체</p>
        </div>
        <button
          onClick={() => setShowBlacklist((prev) => !prev)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.blacklist && statistics.blacklist.length > 0
              ? 'bg-red-50 hover:bg-red-100'
              : 'bg-gray-50'
          } ${showBlacklist ? 'ring-2 ring-red-400' : ''}`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.blacklist && statistics.blacklist.length > 0
                ? 'text-red-700'
                : 'text-gray-400'
            }`}
          >
            {statistics?.blacklist?.length ?? 0}
          </p>
          <p
            className={`text-xs ${
              statistics?.blacklist && statistics.blacklist.length > 0
                ? 'text-red-600'
                : 'text-gray-400'
            }`}
          >
            중복 의심
          </p>
        </button>
        <button
          onClick={() => searchByStatus(GiftCouponRedeemStatus.Rejected)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.rejected ? 'bg-orange-50 hover:bg-orange-100' : 'bg-gray-50'
          } ${
            redeemStatus === GiftCouponRedeemStatus.Rejected && hasSearched
              ? 'ring-2 ring-orange-400'
              : ''
          }`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.rejected ? 'text-orange-700' : 'text-gray-400'
            }`}
          >
            {statistics?.rejected ?? 0}
          </p>
          <p className="text-xs text-orange-600">발송불가</p>
        </button>
        <button
          onClick={() => searchByStatus(GiftCouponRedeemStatus.Canceled)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.canceled ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-50'
          } ${
            redeemStatus === GiftCouponRedeemStatus.Canceled && hasSearched
              ? 'ring-2 ring-gray-400'
              : ''
          }`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.canceled ? 'text-gray-700' : 'text-gray-400'
            }`}
          >
            {statistics?.canceled ?? 0}
          </p>
          <p className="text-xs text-gray-500">취소</p>
        </button>
        <button
          onClick={() => searchByStatus(GiftCouponRedeemStatus.Disposed)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.disposed ? 'bg-slate-100 hover:bg-slate-200' : 'bg-gray-50'
          } ${
            redeemStatus === GiftCouponRedeemStatus.Disposed && hasSearched
              ? 'ring-2 ring-slate-400'
              : ''
          }`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.disposed ? 'text-slate-700' : 'text-gray-400'
            }`}
          >
            {statistics?.disposed ?? 0}
          </p>
          <p className="text-xs text-slate-500">폐기</p>
        </button>
      </div>

      {/* 블랙리스트 */}
      {showBlacklist && statistics?.blacklist && statistics.blacklist.length > 0 && (
        <div className="mx-4 mb-4 overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-red-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium text-red-800">
                중복 의심 번호 ({statistics.blacklist.length}건)
              </p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {statistics.blacklist.map((item) => (
              <div
                key={item.phoneNumber}
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50"
              >
                <button
                  onClick={() => copyToClipboard(item.phoneNumber)}
                  className="shrink-0 font-mono text-sm font-medium text-gray-900 hover:text-indigo-600"
                  title="클릭하여 복사"
                >
                  {formatPhone(item.phoneNumber)}
                </button>
                <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                  {item.count}건
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-1">
                    {item.names.map((name, idx) => (
                      <span
                        key={idx}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                  {item.names.length >= 2 && (
                    <p className="mt-0.5 text-[10px] text-red-500">다른 이름으로 신청</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setPhoneNumber(item.phoneNumber);
                    setRedeemStatus('');
                    setOrdererName('');
                    setOrdererPhone('');
                    setFullCode('');
                    setDateFrom('');
                    setDateTo('');
                    setTimeout(() => {
                      const params: GiftCouponSearchParams = {
                        phoneNumber: item.phoneNumber.replace(/[^0-9]/g, ''),
                      };
                      const key = JSON.stringify(params);
                      setSearchKey(key);
                      setSearchParams(params);
                      setHasSearched(true);
                    }, 0);
                  }}
                  className="shrink-0 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-200"
                >
                  검색
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 검색 폼 */}
      <div className="mx-4 rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">쿠폰번호</label>
            <input
              type="text"
              value={fullCode}
              onChange={(e) => setFullCode(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="쿠폰번호 입력"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              수신자 전화번호
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">주문자명</label>
            <input
              type="text"
              value={ordererName}
              onChange={(e) => setOrdererName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="홍길동"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              주문자 전화번호
            </label>
            <input
              type="tel"
              value={ordererPhone}
              onChange={(e) => setOrdererPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">상태</label>
            <select
              value={redeemStatus}
              onChange={(e) => setRedeemStatus(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">전체 상태</option>
              <option value={GiftCouponRedeemStatus.Available}>교환가능</option>
              <option value={GiftCouponRedeemStatus.Requested}>신청됨</option>
              <option value={GiftCouponRedeemStatus.Delivered}>발송완료</option>
              <option value={GiftCouponRedeemStatus.Rejected}>발송불가</option>
              <option value={GiftCouponRedeemStatus.Expired}>만료</option>
              <option value={GiftCouponRedeemStatus.Refunded}>환불</option>
              <option value={GiftCouponRedeemStatus.Canceled}>취소</option>
              <option value={GiftCouponRedeemStatus.Disposed}>폐기</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">시작일</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">종료일</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
          </div>
        </div>
      </div>

      {/* 검색 결과 */}
      <div className="p-4">
        {hasSearched ? (
          <div className="overflow-hidden rounded-lg bg-white shadow">
            {status === 'loading' ? (
              <div className="center-box py-12">
                <Loading />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-sm text-gray-500">검색 결과: {results.length}건</p>
                  {results.length > 0 && (
                    <button
                      onClick={handleUnshare}
                      disabled={isUnsharing || selectedShareableCount === 0}
                      className="flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isUnsharing
                        ? '처리 중...'
                        : `미전달 처리 (${selectedShareableCount}건)`}
                    </button>
                  )}
                </div>
                {results.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={
                                results.filter(
                                  (c) =>
                                    c.redeemStatus !== GiftCouponRedeemStatus.Available,
                                ).length > 0 &&
                                results
                                  .filter(
                                    (c) =>
                                      c.redeemStatus !== GiftCouponRedeemStatus.Available,
                                  )
                                  .every((c) => selectedIds.has(c._id))
                              }
                              onChange={toggleSelectAll}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            상태
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            공유
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            <div>쿠폰번호</div>
                            <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                              _id
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            상품
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            주문자
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            구매일
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            메모
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            수신자
                          </th>
                          <th
                            className="cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-indigo-600"
                            onClick={() => handleSort()}
                          >
                            <div className="inline-flex items-center gap-1">
                              신청일시
                              <span className="text-indigo-600">
                                {sortDirection === 'desc' ? '▼' : '▲'}
                              </span>
                            </div>
                            <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                              만료일
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            발송일시
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            도매가
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                            액션
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {results.map((coupon) => {
                          const badge = getStatusBadge(coupon.redeemStatus);
                          const choiceUnitName = getChoiceUnitName(coupon);
                          const ordererInfo = getOrdererInfo(coupon);

                          return (
                            <tr
                              key={coupon._id}
                              className={
                                coupon.redeemStatus === GiftCouponRedeemStatus.Disposed
                                  ? 'bg-gray-50 opacity-60'
                                  : coupon.isAdditional || coupon.isReissued
                                  ? 'bg-amber-50/40 hover:bg-amber-50'
                                  : 'hover:bg-gray-50'
                              }
                            >
                              <td className="whitespace-nowrap px-2 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(coupon._id)}
                                  onChange={() => toggleSelect(coupon._id)}
                                  disabled={
                                    coupon.redeemStatus ===
                                    GiftCouponRedeemStatus.Available
                                  }
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30"
                                />
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <span
                                  className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
                                >
                                  {badge.text}
                                </span>
                                {coupon.redeemStatus ===
                                  GiftCouponRedeemStatus.Disposed &&
                                  coupon.disposalReason && (
                                    <p
                                      className="mt-0.5 max-w-[100px] truncate text-[9px] text-slate-400"
                                      title={coupon.disposalReason}
                                    >
                                      {coupon.disposalReason}
                                    </p>
                                  )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                {coupon.shareStatus ===
                                GiftCouponShareStatus.NotShared ? (
                                  <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-500">
                                    미전달
                                  </span>
                                ) : coupon.shareStatus ===
                                  GiftCouponShareStatus.Shared ? (
                                  <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                                    공유됨
                                  </span>
                                ) : coupon.shareStatus ===
                                  GiftCouponShareStatus.Viewed ? (
                                  <span className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">
                                    조회됨
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <div className="flex items-center gap-1.5">
                                  {coupon.isReissued && (
                                    <span className="inline-flex items-center rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
                                      재발급
                                    </span>
                                  )}
                                  {coupon.isAdditional && (
                                    <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                      재발행
                                    </span>
                                  )}
                                  <span className="font-mono text-sm text-gray-700">
                                    {coupon.fullCode || '-'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => copyToClipboard(coupon._id)}
                                  className="mt-0.5 font-mono text-[10px] text-gray-400 hover:text-indigo-600"
                                  title="클릭하여 _id 복사"
                                >
                                  {coupon._id.slice(-6)}
                                </button>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {getImageUrl(coupon) && (
                                    <img
                                      src={getImageUrl(coupon)}
                                      alt=""
                                      className="h-8 w-8 rounded object-cover"
                                    />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {getItemName(coupon)}
                                    </p>
                                    {choiceUnitName && (
                                      <p className="max-w-[180px] truncate text-xs text-indigo-600">
                                        {choiceUnitName}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <p className="text-sm text-gray-900">
                                  {ordererInfo.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatPhone(ordererInfo.phone)}
                                </p>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {formatDate(ordererInfo.purchasedAt)}
                              </td>

                              <td className="max-w-[150px] px-4 py-3">
                                {editingCouponId === coupon._id ? (
                                  <input
                                    type="text"
                                    value={editMemo}
                                    onChange={(e) => setEditMemo(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditPhoneMemo();
                                      if (e.key === 'Escape') cancelEditPhoneMemo();
                                    }}
                                    disabled={isSavingEdit}
                                    placeholder="메모"
                                    className="w-full rounded border border-indigo-300 px-1.5 py-0.5 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  />
                                ) : (
                                  <button
                                    onClick={() => startEditPhoneMemo(coupon)}
                                    className="group flex items-center gap-1 text-left"
                                    title="클릭하여 메모/확인번호 수정"
                                  >
                                    {coupon.memo ? (
                                      <p
                                        className="truncate text-xs text-gray-600"
                                        title={coupon.memo}
                                      >
                                        {coupon.memo}
                                      </p>
                                    ) : (
                                      <span className="text-[10px] text-gray-300">-</span>
                                    )}
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3 w-3 shrink-0 text-gray-300 group-hover:text-indigo-500"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <p className="text-sm text-gray-900">
                                  {formatPhone(coupon.recipient?.phoneNumber)}
                                </p>
                                {coupon.recipient?.name && (
                                  <p className="text-xs text-gray-500">
                                    {coupon.recipient.name}
                                  </p>
                                )}
                                {editingCouponId === coupon._id ? (
                                  <div className="mt-1 flex items-center gap-1">
                                    <input
                                      type="tel"
                                      value={editPhone}
                                      onChange={(e) =>
                                        setEditPhone(
                                          e.target.value.replace(/[^0-9]/g, ''),
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEditPhoneMemo();
                                        if (e.key === 'Escape') cancelEditPhoneMemo();
                                      }}
                                      disabled={isSavingEdit}
                                      placeholder="확인번호"
                                      maxLength={11}
                                      className="w-[100px] rounded border border-amber-300 px-1.5 py-0.5 text-xs focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                                    />
                                    <button
                                      onClick={saveEditPhoneMemo}
                                      disabled={isSavingEdit}
                                      className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {isSavingEdit ? '...' : '저장'}
                                    </button>
                                    <button
                                      onClick={cancelEditPhoneMemo}
                                      disabled={isSavingEdit}
                                      className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-300 disabled:opacity-50"
                                    >
                                      취소
                                    </button>
                                  </div>
                                ) : (
                                  coupon.additionalRecipientPhone && (
                                    <p className="text-xs text-amber-600">
                                      {formatPhone(coupon.additionalRecipientPhone)}
                                    </p>
                                  )
                                )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <div className="text-sm text-gray-700">
                                  {formatDateTime(coupon.recipient?.requestedAt)}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                  {formatDate(coupon.expiresAt)}
                                </div>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered
                                  ? formatDateTime(coupon.gifticon?.deliveredAt)
                                  : '-'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                                {coupon.redeemStatus ===
                                  GiftCouponRedeemStatus.Delivered &&
                                coupon.gifticon?.priceWhole != null
                                  ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                                  : '-'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-center">
                                {coupon.isAdditional && (
                                  <button
                                    onClick={() => handleDeleteAdditional(coupon)}
                                    disabled={deletingId === coupon._id}
                                    className="rounded bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
                                  >
                                    {deletingId === coupon._id
                                      ? '처리중...'
                                      : '재발행폐기'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-gray-500">검색 결과가 없습니다.</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-white p-12 text-center shadow">
            <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">쿠폰 내역 검색</h3>
            <p className="mt-1 text-sm text-gray-500">
              검색 조건을 입력하고 검색 버튼을 눌러주세요.
            </p>
          </div>
        )}
      </div>

      {/* 토스트 */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(8px); }
            15% { opacity: 1; transform: translateY(0); }
            75% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-4px); }
          }
          .animate-fade-in-out {
            animation: fadeInOut 1.2s ease-in-out forwards;
          }
        `}</style>
    </div>
  );
};
